/**
 * Capabilities Service — v3-native permission channel for the frontend.
 *
 * Replaces the legacy ``user.permissions`` array shipped via
 * ``GET /auth/session`` (which only contained tenant-scope grants).
 * The new endpoint returns a per-scope decomposition so page/route/nav
 * gates can answer "does the user have this permission anywhere?" —
 * tenant-wide OR via any org_unit assignment (with subtree
 * inheritance expanded server-side).
 *
 * See ADR-0008 (capabilities-vs-permissions-frozenset) for the
 * architectural rationale.
 */

export interface CapabilitiesResponse {
  user_id: string;
  tenant_id: string | null;
  /** v2-style permission strings granted at scope=tenant. */
  tenant_capabilities: string[];
  /**
   * v2-style permission strings keyed by org_unit id. Inherited
   * descendants are pre-expanded by the backend, so a OrgUnit Head
   * in "Engineering" appears here under every org_unit in the
   * Engineering subtree.
   */
  org_unit_capabilities: Record<string, string[]>;
  generated_at: string;
}

const API_BASE = "/auth";

export const capabilitiesService = {
  /**
   * Fetch the current user's per-scope capabilities.
   *
   * Returns the payload on success, ``null`` when the session is
   * unauthenticated (the endpoint returns 401 — callers usually treat
   * "no capabilities" as the same UI outcome as "not logged in", so
   * surfacing null lets them skip a separate error branch).
   */
  async getCapabilities(): Promise<CapabilitiesResponse | null> {
    const response = await fetch(`${API_BASE}/capabilities`, {
      credentials: "include",
    });
    if (response.status === 401) return null;
    if (!response.ok) {
      throw new Error(
        `Failed to load capabilities (HTTP ${response.status})`,
      );
    }
    return (await response.json()) as CapabilitiesResponse;
  },
};
